import React, { useState } from 'react';
import { useContext } from 'react';
import capitalise from '../../../utils/capitalise';
import AdWrapper from '../../components/AdWrapper';
import { AD_PAGES } from '../../../utils/adsUtils';
import { PrimaryTrainRouteList } from '../../types';
import TrainInfoContext from '../TrainInfoContext';
import { crawlerDuplicateAvoidStyle, isMobile } from '../../../utils/commonUtils';
import { trackClickEventProp61 } from '../../../utils/omnitureUtils';
import { SEO_TRAIN_PAGE, SHOW_ADS_ON_TRAIN_STATIONS_AT_INDEX } from '../../../utils/RailsSEOConstants';

const SEO_TRACKER_EVENT_NAME = 'rail_seo_train_routestations_click';
type TrainStationADTimeProps = {
  time: string;
  children: (isStart: boolean) => JSX.Element;
};
function TrainStationADTime({ time, children }: TrainStationADTimeProps): JSX.Element {
  const startOrEnd = time === '--';
  return children(startOrEnd);
}

type TrainStationListItemProps = {
  station: PrimaryTrainRouteList;
};
function TrainStationListItem({ station }: TrainStationListItemProps) {
  return (
    <div className="railSeoWrap__railStns--rw">
      <span>
        <a
          href={station.stationPageUrl}
          className="stationName"
          onClick={() => trackClickEventProp61(SEO_TRAIN_PAGE, SEO_TRACKER_EVENT_NAME)}
        >
          {station.stationCode}
        </a>
      </span>
      <span>
        <h4 className="desktopShow textBlack">
          <a
            href={station.stationPageUrl}
            className="stationName"
            onClick={() => trackClickEventProp61(SEO_TRAIN_PAGE, SEO_TRACKER_EVENT_NAME)}
          >
            {capitalise(station.stationName)}
          </a>
        </h4>
        <span style={crawlerDuplicateAvoidStyle} className="mobileShow">
          <a
            href={station.stationPageUrl}
            className="stationName"
            onClick={() => trackClickEventProp61(SEO_TRAIN_PAGE, SEO_TRACKER_EVENT_NAME)}
          >
            {capitalise(station.stationName)}
          </a>
        </span>
        <span style={crawlerDuplicateAvoidStyle} className="mobileShow">
          <TrainStationADTime time={station.arrivalTime}>
            {(isStart) => (
              <span className={`font12 ${isStart ? 'lightGreyText' : ''} appendRight8 appendBottom2 latoRegular`}>
                {isStart ? 'Start' : 'ARR' + station.arrivalTime}
              </span>
            )}
          </TrainStationADTime>
          <TrainStationADTime time={station.departureTime}>
            {(isEnd) => (
              <span className={`font12 ${isEnd ? 'lightGreyText' : ''} appendRight8 appendBottom2 latoRegular`}>
                {isEnd ? 'End' : 'DEP' + station.departureTime}
              </span>
            )}
          </TrainStationADTime>
        </span>
      </span>
      <TrainStationADTime time={station.arrivalTime}>
        {(isStart) => (
          <span className={`desktopShow ${isStart ? 'lightGreyText' : ''}`}>
            {isStart ? 'Starts' : station.arrivalTime}
          </span>
        )}
      </TrainStationADTime>
      <TrainStationADTime time={station.departureTime}>
        {(isEnd) => (
          <span className={`desktopShow ${isEnd ? 'lightGreyText' : ''}`}>
            {isEnd ? 'Ends' : station.departureTime}
          </span>
        )}
      </TrainStationADTime>
      <span className="desktopShow">{station.haltTime}</span>
      <span className="desktopShow">{station.dayCount}</span>
      <span className="desktopShow">{station.distance}</span>
      <span style={crawlerDuplicateAvoidStyle} className="mobileShow makeFlex column end">
        <div className="font10 lightGreyText latoRegular appendBottom2">{station.distance}</div>
        <div className="font10 lightGreyText latoRegular">Day {station.dayCount}</div>
      </span>
    </div>
  );
}

type TrainStationsListProps = {
  stations: PrimaryTrainRouteList[];
  title: string;
  displayStationAdForRoute: boolean;
};

function getAd() {
  return (
    <div className={`makeFlex perfectCenter appendTop10 appendBottom10`}>
      {isMobile() && <AdWrapper adsPageName={AD_PAGES.TRAINS_SEO_TRAIN_TEMPLATE_MOBILE_SECOND_FOLD.adsPageName} />}
    </div>
  );
}

function getAdsConditionally(index, displayStationAdForRoute) {
  if (SHOW_ADS_ON_TRAIN_STATIONS_AT_INDEX.includes(index + 1) && displayStationAdForRoute) {
    return getAd();
  }
  return null;
}

function getAdsAtBottom(displayStationAdForRoute, numberOfTrainsLessThanAdsIndex) {
  if (numberOfTrainsLessThanAdsIndex && displayStationAdForRoute) {
    return getAd();
  }
  return null;
}

export default function TrainStationsList({ stations, title, displayStationAdForRoute }: TrainStationsListProps) {
  const trainsData = useContext(TrainInfoContext);
  const isTrainlengthLessThanIndex = stations?.length < SHOW_ADS_ON_TRAIN_STATIONS_AT_INDEX[0];
  const [numberOfTrainsLessThanAdsIndex] = useState(isTrainlengthLessThanIndex);

  return (
    <section>
      <h3 className="railSeoWrap__railStns--title desktopShow capText">{title}</h3>
      <div className="railSeoWrap__railStns">
        <p className="railSeoWrap__railStns--rwTitle">
          <span>{trainsData.primaryTrainRoute['column']['stationCode']}</span>
          <span>{trainsData.primaryTrainRoute['column']['stationName']}</span>
          <span>{trainsData.primaryTrainRoute['column']['arrival']}</span>
          <span>{trainsData.primaryTrainRoute['column']['departure']}</span>
          <span>{trainsData.primaryTrainRoute['column']['haltTime']}</span>
          <span>{trainsData.primaryTrainRoute['column']['day']}</span>
          <span>{trainsData.primaryTrainRoute['column']['distance']}</span>
        </p>
        <div className="railSeoWrap__railStns--table">
          <h3 style={crawlerDuplicateAvoidStyle} className="railSeoWrap__railStns--title mobileShow">
            {trainsData.primaryTrainRoute['column']['STATIONS']}
          </h3>
          {stations.map((s, idx) => {
            return (
              <>
                {getAdsConditionally(idx, displayStationAdForRoute)}
                <TrainStationListItem key={`${s.stationCode}_${idx}`} station={s} />
              </>
            );
          })}
          {getAdsAtBottom(displayStationAdForRoute, numberOfTrainsLessThanAdsIndex)}
        </div>
      </div>
    </section>
  );
}
